import { FC } from "react";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { useCreateUserFavorite } from "@/domain/user/hooks/api/use-create-user-favorite";
import { useDeleteUserFavorite } from "@/domain/user/hooks/api/use-delete-user-favorite";
import { useGetUserFavoriteList } from "@/domain/user/hooks/api/use-get-user-favorite";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { cn } from "@/lib/cn";

interface UserFavoriteButtonProps {
  className?: string;
  pageName?: string;
}

export const UserFavoriteButton: FC<UserFavoriteButtonProps> = ({ className, pageName }) => {
  const { t } = useTranslation("user");
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { isFavorite, getFavoriteByPageUrl } = useGetUserFavoriteList({
    workspaceId: currentWorkspace?.id,
  });
  const { deleteFavorite } = useDeleteUserFavorite();
  const { createFavorite } = useCreateUserFavorite();

  const currentUrl = window.location.pathname;
  const favoriteUrl = currentUrl.replace(`/${currentWorkspace?.workspaceUrl}`, "");
  const isCurrentPageFavorite = isFavorite({ pageUrl: favoriteUrl });
  const currentPageFavorite = getFavoriteByPageUrl({ pageUrl: favoriteUrl });

  const handleToggleFavorite = async () => {
    if (currentPageFavorite) {
      await deleteFavorite({
        id: currentPageFavorite.id,
      });
      toast.success(t("favorites.removedTitle"), { description: t("favorites.removedDescription") });
    } else {
      await createFavorite({
        workspaceId: currentWorkspace?.id,
        pageUrl: favoriteUrl,
        pageName: pageName || t("favorites.defaultPageName"),
      });
      toast.success(t("favorites.addedTitle"), { description: t("favorites.addedDescription") });
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onClick={handleToggleFavorite}
      aria-label={isCurrentPageFavorite ? t("favorites.removeFromFavorites") : t("favorites.addToFavorites")}
    >
      <Star
        className={cn(
          "h-3.5 w-3.5",
          isCurrentPageFavorite ? "fill-current text-yellow-500" : "text-muted-foreground",
        )}
      />
    </Button>
  );
};
